import React, { useEffect, useState, useCallback } from 'react';
import { Button, Form, Modal, Label, Icon, Segment, Header } from 'semantic-ui-react';
import { Grid, GridColumn } from '@progress/kendo-react-grid';
import { SemanticToastContainer, toast } from 'react-semantic-toasts';
import { useDataInspector } from '../../providers/DataInspectorProvider';
import { useAuth } from '../../providers/AuthenticationProvider';
import { useNavigate } from 'react-router';
import { connectorMappingService } from '../../services/ConnectorMappingService';
import moment from 'moment';
import './ConnectorAdmin.css';

const WEB_PAGE = 'Connector Admin';

const LOG_LEVEL_COLORS = {
    'Error': 'red',
    'Warning': 'orange',
    'Information': 'blue',
    'Debug': 'grey',
    'Verbose': 'grey'
};

const getConnectorStatus = (connector) => {
    if (!connector.lastHeartbeat) {
        return { status: 'unknown', color: 'grey', label: 'Unknown' };
    }
    const lastHeartbeat = moment(connector.lastHeartbeat);
    const minutesAgo = moment().diff(lastHeartbeat, 'minutes');

    if (minutesAgo < 10) {
        return { status: 'online', color: 'green', label: 'Online' };
    } else if (minutesAgo < 30) {
        return { status: 'warning', color: 'yellow', label: 'Delayed' };
    } else {
        return { status: 'offline', color: 'red', label: 'Offline' };
    }
};

export const ConnectorAdmin = () => {
    const { checkAuth, getUserType } = useAuth();
    const navigate = useNavigate();

    const [groupId, setGroupId] = useState();
    const [groupOptions, setGroupOptions] = useState([]);
    const [connectors, setConnectors] = useState([]);
    const [selectedConnector, setSelectedConnector] = useState(null);
    const [logsModalOpen, setLogsModalOpen] = useState(false);
    const [logs, setLogs] = useState([]);
    const [logsTotal, setLogsTotal] = useState(0);
    const [logsPage, setLogsPage] = useState(0);
    const [logsLevel, setLogsLevel] = useState(null);
    const [logsLoading, setLogsLoading] = useState(false);

    const { getDataInspectorGroups, groups } = useDataInspector();

    useEffect(() => {
        if (checkAuth(getUserType(), WEB_PAGE)) {
            navigate('/');
        }
    }, [checkAuth, getUserType, navigate]);

    useEffect(() => {
        getDataInspectorGroups();
    }, [getDataInspectorGroups]);

    useEffect(() => {
        const tempOptions = groups.map(group => ({
            key: group.id,
            value: group.groupId,
            text: group.name,
        }));
        setGroupOptions(tempOptions);
    }, [groups]);

    const loadConnectors = useCallback(async () => {
        if (!groupId) {
            setConnectors([]);
            return;
        }
        const data = await connectorMappingService.getConnectors(groupId);
        setConnectors(data || []);
    }, [groupId]);

    useEffect(() => {
        loadConnectors();
    }, [loadConnectors]);

    useEffect(() => {
        if (!groupId) return;
        const interval = setInterval(loadConnectors, 60000);
        return () => clearInterval(interval);
    }, [groupId, loadConnectors]);

    const handleGroupChange = (value) => {
        setGroupId(value);
        setConnectors([]);
        setSelectedConnector(null);
    };

    const handleViewLogs = async (connector) => {
        setSelectedConnector(connector);
        setLogsPage(0);
        setLogsLevel(null);
        setLogsModalOpen(true);
        await loadLogs(connector.id, 0, null);
    };

    const loadLogs = async (connectorId, page, level) => {
        setLogsLoading(true);
        try {
            const { logs: logData, totalCount } = await connectorMappingService.getLogs(
                groupId,
                connectorId,
                50,
                page * 50,
                level
            );
            setLogs(logData);
            setLogsTotal(totalCount);
        } catch (e) {
            toast({
                type: 'error',
                description: 'Failed to load logs',
                animation: 'fade down',
                time: 5000,
            });
        }
        setLogsLoading(false);
    };

    const handleLogsPageChange = (event) => {
        const newPage = event.page.skip / 50;
        setLogsPage(newPage);
        if (selectedConnector) {
            loadLogs(selectedConnector.id, newPage, logsLevel);
        }
    };

    const handleLogsLevelChange = (value) => {
        setLogsLevel(value);
        setLogsPage(0);
        if (selectedConnector) {
            loadLogs(selectedConnector.id, 0, value);
        }
    };

    const handleCloseLogsModal = () => {
        setLogsModalOpen(false);
        setSelectedConnector(null);
        setLogs([]);
        setLogsTotal(0);
    };

    return (
        <div className="connector-admin-content">
            <Header as="h2">Connector Administration</Header>
            <Segment>
                <Form>
                    <Form.Group>
                        <Form.Dropdown
                            width={6}
                            style={{ height: '38px' }}
                            value={groupId}
                            label="Group"
                            placeholder="Select Group"
                            options={groupOptions}
                            onChange={(e, { value }) => handleGroupChange(value)}
                            selection
                            search
                            required
                        />
                        <Form.Field width={4} style={{ display: 'flex', alignItems: 'flex-end' }}>
                            <Button
                                icon
                                labelPosition="left"
                                onClick={loadConnectors}
                                disabled={!groupId}
                            >
                                <Icon name="refresh" />
                                Refresh
                            </Button>
                        </Form.Field>
                    </Form.Group>
                </Form>
            </Segment>

            {groupId && (
                <Segment>
                    <Header as="h3">Connectors</Header>
                    {connectors.length === 0 ? (
                        <p>No connectors found for this group.</p>
                    ) : (
                        <Grid data={connectors}>
                            <GridColumn
                                field="status"
                                title="Status"
                                width="100px"
                                cell={props => {
                                    const { color, label } = getConnectorStatus(props.dataItem);
                                    return (
                                        <td>
                                            <Label color={color} size="small">{label}</Label>
                                        </td>
                                    );
                                }}
                            />
                            <GridColumn field="connectorName" title="Name" />
                            <GridColumn field="version" title="Version" width="100px" />
                            <GridColumn
                                field="lastHeartbeat"
                                title="Last Heartbeat"
                                cell={props => (
                                    <td>
                                        {props.dataItem.lastHeartbeat
                                            ? moment(props.dataItem.lastHeartbeat).format('YYYY-MM-DD HH:mm:ss')
                                            : 'Never'}
                                    </td>
                                )}
                            />
                            <GridColumn
                                field="deviceCount"
                                title="Devices"
                                width="80px"
                            />
                            <GridColumn
                                field="pointCount"
                                title="Points"
                                width="80px"
                            />
                            <GridColumn
                                title="Actions"
                                width="150px"
                                cell={props => (
                                    <td>
                                        <Button
                                            size="mini"
                                            icon="file text"
                                            content="Logs"
                                            onClick={() => handleViewLogs(props.dataItem)}
                                        />
                                    </td>
                                )}
                            />
                        </Grid>
                    )}
                </Segment>
            )}

            <Modal
                open={logsModalOpen}
                onClose={handleCloseLogsModal}
                size="fullscreen"
            >
                <Modal.Header>
                    Connector Logs - {selectedConnector?.connectorName}
                </Modal.Header>
                <Modal.Content scrolling>
                    <Form>
                        <Form.Group>
                            <Form.Dropdown
                                width={4}
                                style={{ height: '38px' }}
                                label="Filter by Level"
                                placeholder="All Levels"
                                clearable
                                selection
                                value={logsLevel}
                                onChange={(e, { value }) => handleLogsLevelChange(value)}
                                options={[
                                    { key: 'error', value: 'Error', text: 'Error' },
                                    { key: 'warning', value: 'Warning', text: 'Warning' },
                                    { key: 'info', value: 'Information', text: 'Information' },
                                    { key: 'debug', value: 'Debug', text: 'Debug' },
                                ]}
                            />
                        </Form.Group>
                    </Form>

                    <Grid
                        data={logs}
                        total={logsTotal}
                        skip={logsPage * 50}
                        take={50}
                        pageable={true}
                        onPageChange={handleLogsPageChange}
                    >
                        <GridColumn
                            field="level"
                            title="Level"
                            width="100px"
                            cell={props => (
                                <td>
                                    <Label
                                        color={LOG_LEVEL_COLORS[props.dataItem.level] || 'grey'}
                                        size="small"
                                    >
                                        {props.dataItem.level}
                                    </Label>
                                </td>
                            )}
                        />
                        <GridColumn
                            field="loggedAt"
                            title="Timestamp"
                            width="180px"
                            cell={props => (
                                <td>
                                    {moment(props.dataItem.loggedAt).format('YYYY-MM-DD HH:mm:ss')}
                                </td>
                            )}
                        />
                        <GridColumn
                            field="message"
                            title="Message"
                            cell={props => (
                                <td className="log-message-cell">
                                    {props.dataItem.message}
                                </td>
                            )}
                        />
                    </Grid>

                    {logsLoading && (
                        <Segment loading basic />
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button onClick={handleCloseLogsModal}>Close</Button>
                </Modal.Actions>
            </Modal>

            <SemanticToastContainer position="bottom-center" />
        </div>
    );
};
